import React, { useRef } from "react";
import { Download, Loader2, Music, Play, Square, Trash2, Volume2, X } from "lucide-react";
import type { BackgroundSound } from "@/api/client";

type GalleryTab = "Sons ambiants" | "Effets sonores" | "Mes sons";

type GalleryModalProps = {
  open: boolean;
  onClose: () => void;
  activeTab: GalleryTab;
  setActiveTab: (tab: GalleryTab) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  soundsQuery: { isLoading: boolean };
  filteredSounds: BackgroundSound[];
  ambientPathSet: Set<string>;
  sfxPathSet: Set<string>;
  playingPreview: string | null;
  togglePreview: (sound: BackgroundSound) => void;
  addClip: (path: string, kind: "ambient" | "sfx") => void;
  onDeleteSound: (sound: BackgroundSound) => void;
  deletingSoundPath: string | null;

  // Pending upload states and callbacks
  pendingFile: File | null;
  setPendingFile: (file: File | null) => void;
  pendingTitle: string;
  setPendingTitle: (title: string) => void;
  pendingKind: "ambient" | "sfx";
  setPendingKind: (kind: "ambient" | "sfx") => void;
  isUploadingPending: boolean;
  pendingDuration: number | null;
  pendingPlaying: boolean;
  togglePendingPreview: () => void;
  handlePendingDrop: (e: React.DragEvent) => void;
  handleImportPendingSound: () => void;
};

// Simple visual format helpers
function fmtTime(s: number): string {
  const hh = Math.floor(s / 3600).toString().padStart(2, "0");
  const mm = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const ss = Math.floor(s % 60).toString().padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function mixTagStyle(tag: string): React.CSSProperties {
  if (tag === "Idéal fond") return { background: "#cdffd1", color: "#04a404" };
  if (tag === "Idéal effet") return { background: "#fdebf9", color: "#c8009c" };
  return { background: "#eff6ff", color: "#3b6fb0" };
}

export function GalleryModal({
  open,
  onClose,
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  soundsQuery,
  filteredSounds,
  ambientPathSet,
  sfxPathSet,
  playingPreview,
  togglePreview,
  addClip,
  onDeleteSound,
  deletingSoundPath,
  pendingFile,
  setPendingFile,
  pendingTitle,
  setPendingTitle,
  pendingKind,
  setPendingKind,
  isUploadingPending,
  pendingDuration,
  pendingPlaying,
  togglePendingPreview,
  handlePendingDrop,
  handleImportPendingSound,
}: GalleryModalProps) {
  const pendingFileInputRef = useRef<HTMLInputElement>(null);
  const [isPendingDragOver, setIsPendingDragOver] = React.useState(false);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-[650px] rounded-[20px] border border-[#cbd5e1] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] relative overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 bg-white">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[20px] font-semibold text-[#0F172B]">♫ Galerie audio</span>
            </div>
            <p className="text-[14px] text-[#45556C]">
              Choisissez les audios qui correspondent le mieux à vos objectifs de médiation.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#45556c] transition-colors shrink-0"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content area */}
        <div className="p-6 flex flex-col gap-4 overflow-y-auto">
          {/* Segmented control tab buttons */}
          <div className="flex items-center">
            <div className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white p-1 select-none">
              {["Sons ambiants", "Effets sonores", "Mes sons"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab as GalleryTab)}
                  className={`rounded-full px-4 py-1.5 text-[14px] font-medium transition-colors ${
                    activeTab === tab
                      ? "border border-[#007AFF] bg-[#EFF6FF] text-[#0F172B]"
                      : "text-[#45556C] hover:text-[#0F172B]"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="relative flex items-center rounded-full border border-[#E2E8F0] px-4 py-2.5 bg-white focus-within:ring-2 focus-within:ring-[#007AFF]">
            <Search className="h-4 w-4 text-[#94A3B8] mr-2 shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un son"
              className="flex-1 bg-transparent text-[14px] text-[#0F172B] outline-none placeholder:text-[#94A3B8]"
            />
          </div>

          {/* Sound list */}
          <div className="flex-1 overflow-y-auto min-h-[220px] max-h-[320px] border border-[#E2E8F0] rounded-[16px] bg-white divide-y divide-[#E2E8F0] custom-scrollbar">
            {filteredSounds.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-slate-400">
                <Music className="h-8 w-8 text-slate-300 mb-2" />
                <span className="text-[14px]">Aucun son trouvé</span>
              </div>
            ) : (
              filteredSounds.map((sound) => {
                const isPlaying = playingPreview === sound.path;
                const durationText = sound.duration ? fmtTime(sound.duration) : "";
                const resolvedKind = sound.suggested_kind || (sound.duration && sound.duration >= 15 ? "ambient" : "sfx");
                
                return (
                  <div key={sound.path} className="flex items-center justify-between p-3.5 hover:bg-[#F8FAFC] transition-colors gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={() => togglePreview(sound)}
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] text-[#0F172B] transition-colors hover:bg-[#eef2f7] ${
                          isPlaying ? "bg-[#EFF6FF] text-[#007AFF] border-[#007AFF]" : "bg-white"
                        }`}
                        aria-label={isPlaying ? "Mettre en pause la prévisualisation" : "Écouter la prévisualisation"}
                      >
                        {isPlaying ? (
                          <Square className="h-3.5 w-3.5 fill-current" />
                        ) : (
                          <Play className="h-3.5 w-3.5 fill-current ml-0.5" />
                        )}
                      </button>
                      
                      <div className="flex flex-col min-w-0">
                        <span className="text-[14px] font-semibold text-[#0F172B] truncate">{sound.name}</span>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {durationText && (
                            <span className="text-[12px] text-[#45556C] font-mono">{durationText}</span>
                          )}
                          {sound.category && (
                            <span className="text-[11px] text-[#94A3B8] font-normal">· {sound.category}</span>
                          )}
                          {sound.mix_tags?.map((tag) => (
                            <span
                              key={tag}
                              style={mixTagStyle(tag)}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {sound.imported && (
                        <button
                          type="button"
                          onClick={() => onDeleteSound(sound)}
                          disabled={deletingSoundPath === sound.path}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#FF3B30]/20 bg-[#FFF5F4] text-[#FF3B30] transition-colors hover:bg-[#FFE9E7] disabled:opacity-60"
                          aria-label={`Supprimer ${sound.name}`}
                          title="Supprimer ce son"
                        >
                          {deletingSoundPath === sound.path ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          addClip(sound.path, resolvedKind);
                          onClose();
                        }}
                        className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-[#EFF6FF] border border-[#007AFF]/25 px-3 text-xs font-semibold text-[#007AFF] transition-colors hover:bg-[#007AFF] hover:text-white"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Configure Pending Selected Audio Card */}
          {pendingFile && (
            <div className="rounded-[16px] border border-[#E2E8F0] bg-[#F8FAFC] p-4 flex flex-col gap-4 border-dashed">
              <div className="flex items-center justify-between text-[12px] text-[#45556C]">
                <span className="font-semibold text-[14px]">Nom du son</span>
                <span className="text-[#94A3B8]">
                  {pendingDuration ? fmtTime(pendingDuration) : "--:--"}{" "}
                  ·{" "}
                  {(pendingFile.size / (1024 * 1024)).toFixed(1).replace(".", ",") + " Mo"}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={togglePendingPreview}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-transparent text-[#0F172B] hover:bg-[#E2E8F0]/40"
                  aria-label={pendingPlaying ? "Arrêter la lecture" : "Écouter le son"}
                >
                  {pendingPlaying ? (
                    <Square className="h-4 w-4 fill-[#0F172B]" />
                  ) : (
                    <Play className="h-4 w-4 fill-[#0F172B] ml-0.5" />
                  )}
                </button>
                <input
                  type="text"
                  value={pendingTitle}
                  onChange={(e) => setPendingTitle(e.target.value)}
                  className="flex-1 min-w-0 h-10 rounded-xl px-3 text-[14px] text-[#0F172B] bg-transparent outline-none focus:ring-1 focus:ring-[#007AFF]"
                  placeholder="Nom du son"
                />
                <button
                  type="button"
                  onClick={() => setPendingFile(null)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-transparent text-[#FF3B30] hover:bg-[#FFF1F0]/50 transition-colors"
                  aria-label="Supprimer le son sélectionné"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[14px] font-semibold text-[#0F172B]">Type de son</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingKind("ambient")}
                    className={`px-4 py-1.5 rounded-full border text-[14px] font-medium transition-all flex items-center gap-1.5 ${
                      pendingKind === "ambient"
                        ? "bg-[#EFF6FF] border-[#007AFF] text-[#007AFF]"
                        : "bg-white border-[#E2E8F0] text-[#45556C] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <Music className="h-4 w-4" />
                    Son ambiants
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingKind("sfx")}
                    className={`px-4 py-1.5 rounded-full border text-[14px] font-medium transition-all flex items-center gap-1.5 ${
                      pendingKind === "sfx"
                        ? "bg-[#EFF6FF] border-[#007AFF] text-[#007AFF]"
                        : "bg-white border-[#E2E8F0] text-[#45556C] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <Volume2 className="h-4 w-4" />
                    Effet sonore
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setPendingFile(null)}
                  className="inline-flex h-[38px] items-center rounded-full border border-[#007AFF] bg-white px-5 text-[14px] font-medium text-[#007AFF] hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={isUploadingPending}
                  onClick={handleImportPendingSound}
                  className="inline-flex h-[38px] items-center gap-1.5 rounded-full bg-[#007AFF] px-5 text-[14px] font-medium text-white hover:bg-[#006ae0] transition-colors disabled:opacity-60"
                >
                  {isUploadingPending ? (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  ) : (
                    <>
                      Importer le son
                      <span className="font-semibold text-white ml-0.5">❯</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Drag and Drop Zone at the Bottom */}
          <input
            ref={pendingFileInputRef}
            type="file"
            accept=".mp3,.wav,.m4a,audio/*"
            className="hidden"
            onChange={(e) => {
              const selectedFile = e.target.files?.[0] ?? null;
              if (selectedFile) {
                setPendingFile(selectedFile);
                setPendingTitle(selectedFile.name.replace(/\.[^.]+$/, ""));
              }
            }}
          />
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsPendingDragOver(true);
            }}
            onDragLeave={() => setIsPendingDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsPendingDragOver(false);
              handlePendingDrop(e);
            }}
            onClick={() => pendingFileInputRef.current?.click()}
            className={`flex min-h-[140px] w-full flex-col items-center justify-center gap-2 rounded-[16px] border border-dashed text-center cursor-pointer transition-colors p-4 ${
              isPendingDragOver
                ? "border-[#007AFF] bg-[#EFF6FF]"
                : "border-[#8EA4BD]/40 bg-[#EFF6FF]/20 hover:bg-[#EFF6FF]/40"
            }`}
          >
            <Download className="size-8 text-[#45556C]" />
            <div className="space-y-0.5">
              <p className="text-[16px] font-semibold text-[#45556C]">Cliquez pour importer un son</p>
              <p className="text-[12px] text-[#8EA4BD]">MP3, WAV, M4A jusqu'à 100MB</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Re-export Search component placeholder to satisfy TSX imports
function Search(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
